import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import {
  quarterOf,
  savingsOf,
  useFormat,
  useModernization,
  usePayback,
  type ModernPackage,
  type Modernization as Data,
  type Quarter,
} from '../components/modernization.js';
import { MIB, parseCount, parseEuros, parsePercent, type TileStatus } from '../components/site.js';
import { Button, LinkButton, LoadError, Loading, TextField } from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { intlLocale } from '../i18n.js';
import { HEAVY_MB, rate } from './Speed.js';

/**
 * Modernization (T-39, site-modernization.html): what AI saves, the packages by effort and benefit,
 * what each costs and when it pays off, what we found, and the customer's own figures.
 */
export function Modernization() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const query = useModernization(siteId);

  if (query.isPending) return <Loading />;
  if (query.isError)
    return (
      <LoadError message={errorMessage(query.error, t)} onRetry={() => void query.refetch()} />
    );
  const m = query.data;

  return (
    <>
      <div className="hd">
        <div>
          <h1>{t('modern.title')}</h1>
          <div className="sub">
            <span>{t('modern.intro')}</span>
          </div>
        </div>
      </div>
      {m.packages.length === 0 ? (
        <div className="empty">
          <b>{t('modern.nothing')}</b>
        </div>
      ) : (
        <>
          <Savings m={m} />
          <Matrix packages={m.packages} />
          <Packages siteId={siteId} packages={m.packages} lcpMs={m.facts.lcpMs} />
        </>
      )}
      <Facts m={m} />
      <Figures siteId={siteId} m={m} />
      <Extras m={m} />
    </>
  );
}

/** "Mögliche Ersparnis mit KI": all packages classic, with AI, and the difference (decision 2). */
function Savings({ m }: { m: Data }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  const s = savingsOf(m.packages);
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>
        {t('modern.saving.title')}{' '}
        <small>{t('modern.saving.hint', { rate: f.euro(m.figures.hourlyRate) })}</small>
      </h2>
      <dl className="kv">
        <div>
          <dt>{t('modern.saving.classic')}</dt>
          <dd className="mono">{f.effort(s.classic.hours, s.classic.cost)}</dd>
        </div>
        <div>
          <dt>{t('modern.saving.ai')}</dt>
          <dd className="mono good">{f.effort(s.ai.hours, s.ai.cost)}</dd>
        </div>
        <div>
          <dt>{t('modern.saving.saved')}</dt>
          <dd className="mono good">
            {f.euroRange(s.saved)} ({f.percent([s.share, s.share])})
          </dd>
        </div>
      </dl>
      <p className="spec">
        {s.withoutRelaunch ? `${t('modern.saving.noRelaunch')} ` : null}
        {t('modern.how.ai')}
      </p>
    </section>
  );
}

const QUARTERS: readonly [Quarter, string][] = [
  ['quick', 'quickWins'],
  ['big', 'bigProjects'],
  ['later', 'later'],
  ['skip', 'skip'],
];

/** Effort and benefit as four quarters (.matrix): the quick wins first. */
function Matrix({ packages }: { packages: readonly ModernPackage[] }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>{t('modern.matrix.title')}</h2>
      <ul className="matrix">
        {QUARTERS.map(([quarter, label]) => {
          const inside = packages.filter((p) => quarterOf(p) === quarter);
          return (
            <li key={quarter} className={quarter === 'quick' ? 'q1' : undefined}>
              <b>{t(`modern.matrix.${label}`)}</b>
              {inside.length === 0 ? (
                <span>{t('modern.matrix.none')}</span>
              ) : (
                inside.map((p) => (
                  <span key={p.key}>
                    {t('modern.matrix.item', {
                      title: t(`modern.packages.${p.key}.title`),
                      hours: f.hours(p.hours.ai),
                    })}
                  </span>
                ))
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** The packages (.pkgs): cost with AI and classic, payback, and the way to each one. */
function Packages({
  siteId,
  packages,
  lcpMs,
}: {
  siteId: string;
  packages: readonly ModernPackage[];
  lcpMs: number | null;
}) {
  const { t } = useTranslation();
  const f = useFormat();
  const payback = usePayback();
  const id = useId();
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>{t('modern.packagesTitle')}</h2>
      <div className="pkgs">
        {packages.map((p) => (
          <div key={p.key} className={p.recommended ? 'pkg rec' : 'pkg'}>
            {p.recommended ? (
              <span className="badge acc nodot">{t('modern.recommended')}</span>
            ) : null}
            <h3>{t(`modern.packages.${p.key}.title`)}</h3>
            <p className="bodyM">{t(`modern.packages.${p.key}.summary`)}</p>
            <div className="cost">
              <div>
                {t('modern.costAi')}
                <strong className="ok">{f.euroRange(p.cost.ai)}</strong>
                <span>{f.hours(p.hours.ai)}</span>
              </div>
              <div>
                {t('modern.costClassic')}
                <strong>{f.euroRange(p.cost.classic)}</strong>
                <span>{f.hours(p.hours.classic)}</span>
              </div>
              <div>
                {t('modern.payback.label')}
                <strong>{payback(p, lcpMs)}</strong>
              </div>
            </div>
            <LinkButton
              to={`/sites/${siteId}/modernization/${p.key}`}
              variant={p.recommended ? 'primary' : 'secondary'}
              size="sm"
            >
              {t('modern.details')}
            </LinkButton>
          </div>
        ))}
      </div>
      {packages.length > 1 ? (
        <Link className="more" to={`/sites/${siteId}/modernization/compare`}>
          {t('modern.compareLink')}
        </Link>
      ) : null}
      <p className="spec">{t('modern.disclaimer')}</p>
    </section>
  );
}

/** "Was wir gefunden haben": the measured values, rated as on the speed page. */
function Facts({ m }: { m: Data }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  const x = m.facts;
  const rows: [string, string, TileStatus][] = [];
  if (x.score !== null)
    rows.push([t('modern.facts.score'), `${x.score} / 100`, rate('score', x.score)]);
  if (x.lcpMs !== null)
    rows.push([t('modern.facts.lcp'), f.seconds(x.lcpMs), rate('lcpMs', x.lcpMs)]);
  // One limit and one unit for the weight everywhere: 3 MiB, yellow above (decision 5).
  if (x.pageBytes !== null)
    rows.push([
      t('modern.facts.weight'),
      f.megabytes(x.pageBytes),
      x.pageBytes > HEAVY_MB * MIB ? 'warn' : 'good',
    ]);
  if (x.unusedJsMs !== null && x.unusedJsMs >= 300)
    rows.push([t('modern.facts.unusedJs'), f.seconds(x.unusedJsMs), 'warn']);
  if (x.ttfbMs !== null)
    rows.push([t('modern.facts.ttfb'), f.seconds(x.ttfbMs), rate('ttfbMs', x.ttfbMs)]);
  if (x.co2Grams !== null)
    rows.push([
      t('modern.facts.co2'),
      t('modern.grams', { value: f.num(x.co2Grams) }),
      x.co2Grams <= x.co2MedianGrams ? 'good' : 'warn',
    ]);
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>{t('modern.facts.title')}</h2>
      {rows.length === 0 ? (
        <p className="bodyM">{t('modern.facts.notMeasured')}</p>
      ) : (
        <dl className="kv">
          {rows.map(([label, value, status]) => (
            <div key={label}>
              <dt>{label}</dt>
              <dd className={status === 'unknown' ? 'mono' : `mono ${status}`}>
                {value}
                {/* Not by colour alone (WCAG 1.4.1): the rating is read out with the value. */}
                {status === 'unknown' ? null : (
                  <span className="sr-only"> – {t(`tileStatus.${status}`)}</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

/** Assumed hourly rate (v2 §19): not a quoted price; the customer can enter their own. */
const DEFAULT_RATE = 79;

/** Euros as typed («1.234,50», «79 €»; SP-A2.222): null when empty, undefined when unreadable. */
const toEuros = (v: string) => {
  const cents = parseEuros(v);
  return cents == null ? cents : cents / 100;
};

/**
 * Each figure as saved, read by its kind (SP-A2.222): euros, a count («1.500» visitors) or per
 * cent, with the error its field says when the figure cannot be read.
 */
const FIGURES = {
  rate: { saved: 'hourlyRate', read: toEuros, invalid: 'app.amountInvalid' },
  visitors: { saved: 'monthlyVisitors', read: parseCount, invalid: 'app.countInvalid' },
  conversion: { saved: 'conversionPct', read: parsePercent, invalid: 'app.percentInvalid' },
  order: { saved: 'avgOrderEur', read: toEuros, invalid: 'app.amountInvalid' },
} as const;
type Figure = keyof typeof FIGURES;
const KEYS = Object.keys(FIGURES) as Figure[];

/** "Ihre Zahlen" (.calc): with them the payback is in months. */
function Figures({ siteId, m }: { siteId: string; m: Data }) {
  const { t, i18n } = useTranslation();
  const f = useFormat();
  const id = useId();
  const client = useQueryClient();
  // At most two decimals, as the fields read them («1,234» would be refused, SP-A2.222), rounded
  // as written: Intl shows 1.005 as «1,01», not as the binary 1.00499…
  const show = (v: number | null) =>
    v === null
      ? ''
      : new Intl.NumberFormat(intlLocale(i18n.language), {
          maximumFractionDigits: 2,
          useGrouping: false,
        }).format(v);
  const saved = (key: Figure) => m.figures[FIGURES[key].saved];
  const shown = () =>
    Object.fromEntries(KEYS.map((key) => [key, show(saved(key))])) as Record<Figure, string>;
  const [form, setForm] = useState(shown);
  useEffect(() => {
    setForm(shown());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.figures]);
  // A field still showing its figure keeps it as saved, not rounded to what the field shows
  // (review of SP-A2.222); one typed in is read.
  const read = (key: Figure) =>
    form[key] === show(saved(key)) ? saved(key) : FIGURES[key].read(form[key]);
  // A figure not read is said at its field once saving was tried, until it reads; nothing is
  // saved meanwhile – an unreadable figure was saved as none and erased the one before.
  const [tried, setTried] = useState(false);
  const fields = useRef<Partial<Record<Figure, HTMLInputElement | null>>>({});
  const error = (key: Figure) =>
    tried && read(key) === undefined ? t(FIGURES[key].invalid) : undefined;
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        api.PUT('/sites/{siteId}/business-figures', {
          params: { path: { siteId } },
          body: {
            hourlyRate: read('rate') ?? DEFAULT_RATE,
            monthlyVisitors: read('visitors') ?? null,
            conversionPct: read('conversion') ?? null,
            avgOrderEur: read('order') ?? null,
          },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['site', siteId, 'modernization'] }),
  });
  const field = (key: Figure, label: string, inputMode: 'decimal' | 'numeric') => (
    <TextField
      ref={(input) => {
        fields.current[key] = input;
      }}
      label={label}
      inputMode={inputMode}
      value={form[key]}
      error={error(key)}
      onChange={(e) => setForm({ ...form, [key]: e.target.value })}
    />
  );
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>
        {t('modern.figures.title')} <small>{t('modern.figures.hint')}</small>
      </h2>
      <form
        className="calc"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const wrong = KEYS.find((key) => read(key) === undefined);
          if (wrong) {
            setTried(true);
            fields.current[wrong]?.focus();
            return;
          }
          save.mutate();
        }}
      >
        {field('rate', t('modern.figures.rate'), 'decimal')}
        {field('visitors', t('modern.figures.visitors'), 'numeric')}
        {field('conversion', t('modern.figures.conversion'), 'decimal')}
        {field('order', t('modern.figures.order'), 'decimal')}
        <Button type="submit" variant="secondary" size="sm" busy={save.isPending}>
          {t('modern.figures.save')}
        </Button>
      </form>
      {save.isError ? <p role="alert">{errorMessage(save.error, t)}</p> : null}
      {m.monthlyRevenue !== null ? (
        <p className="bodyM">{t('modern.figures.revenue', { value: f.euro(m.monthlyRevenue) })}</p>
      ) : null}
      <p className="spec">
        {t('modern.how.title')}: {t('modern.how.benefit')} {t('modern.figures.rateHint')}
      </p>
    </section>
  );
}

/** Further checks of the code (audit §3): accessibility, the Google profile, CO₂. */
function Extras({ m }: { m: Data }) {
  const { t } = useTranslation();
  const f = useFormat();
  const id = useId();
  const co2 = m.facts.co2Grams;
  const items: { key: string; tone: string; label: string; text: string }[] = [
    {
      key: 'bfsg',
      tone: m.extras.bfsg === 'warn' ? 'warn' : 'neutral',
      label: t(m.extras.bfsg === 'warn' ? 'modern.extras.found' : 'modern.extras.notChecked'),
      text: t('modern.extras.bfsg'),
    },
    {
      key: 'local',
      tone: m.extras.localBusiness === 'bad' ? 'bad' : 'neutral',
      label: t(
        m.extras.localBusiness === 'bad' ? 'modern.extras.missing' : 'modern.extras.notChecked',
      ),
      text: t('modern.extras.local'),
    },
  ];
  if (co2 !== null)
    items.push({
      key: 'co2',
      tone: co2 <= m.facts.co2MedianGrams ? 'ok' : 'warn',
      label: t('modern.grams', { value: f.num(co2) }),
      text: `${t('modern.co2', { value: f.num(co2), median: f.num(m.facts.co2MedianGrams) })}. ${t('modern.extras.co2')}`,
    });
  return (
    <section className="card" aria-labelledby={id}>
      <h2 id={id}>{t('modern.extras.title')}</h2>
      <ul className="checks extras">
        {items.map((c) => (
          <li key={c.key}>
            <span className={`badge ${c.tone} nodot`}>{c.label}</span>
            <div>
              <b>{t(`modern.extras.${c.key}Title`)}</b>
              <span>{c.text}</span>
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}
